import { replacePlaceholdersInString } from '@utils/stringUtilities';
import { local } from '@utils/storage';
import { StorageKeys } from '@constants/storageKeys';
import { OPTIONAL_KEY } from '@components/features/lookup/hooks/useFetchLookup';

type Params = {
    [key: string]: string;
};

export function createAuthenticateApiPath(tenantId?: string): string {
    const tenant = tenantId || local.getItem(StorageKeys.TenantId);

    let domain = `https://${tenant}.workflowindecloud.nl/api`;

    if (tenant?.toLowerCase() === 'local') {
        domain = 'https://localhost:44325/api';
    }

    if (tenant?.toLowerCase() === 'dev') {
        domain = 'https://devweb.docbaseweb.nl/api';
    }

    if (tenant?.toLowerCase() === 'test') {
        domain = 'https://testweb.docbaseweb.nl/api';
    }

    if (tenant?.toLowerCase() === 'acc') {
        domain = 'https://accweb.docbaseweb.nl/api';
    }

    return `${domain}/authenticate`;
}

// User
export function createFetchCurrentUserPath(): string {
    return '/profile';
}

export function createUpdateCurrentUserPath(): string {
    return '/profile';
}

// Docclasses
export function createFetchDocClassesPath(): string {
    return '/docclasses';
}

// SuperVisor Actions
export function createFetchSuperVisorActionsPath(documentId: number): string {
    return `/supervisor/actions?documentId=${documentId}`;
}

export function createPerformSuperVisorActionPath(documentId: number): string {
    return `/supervisor/actions?documentId=${documentId}`;
}

// Search
export function createSearchDocumentPath(docClass: string): string {
    return `/documentlist/search?docclass=${docClass}`;
}

export function createFetchSearchFieldsPath(docClass: string): string {
    return `/documentlist/searchfields?docclass=${docClass}`;
}

// Doclists
export function createFetchDocListsPath(docClass: string): string {
    return `/documentlists?docclass=${docClass}`;
}

export function createFetchDocListPath(docClass: string, listType: string): string {
    return `/documentlist?docclass=${docClass}&listtype=${listType}`;
}

export function createFetchDocListCountPath(docClass: string, listType: string): string {
    return `/documentlist/count?docclass=${docClass}&listtype=${listType}`;
}

// Document
export function createFetchDocumentPath(documentId: number, editMode?: boolean): string {
    return `/document?documentid=${documentId.toString()}&editmode=${editMode ? 'true' : 'false'}`;
}

export function createValidateDocumentPath(documentId: number, actionId: string): string {
    return `/document/validate?documentid=${documentId.toString()}&actionId=${actionId}`;
}

// Document headers
export function createFetchDocumentHeadersPath(documentId: number): string {
    return `/document/headers?documentid=${documentId.toString()}`;
}

export function createUpdateDocumentHeadersPath(documentId: number): string {
    return `/document/headers?documentid=${documentId.toString()}`;
}

// Document external info
export function createFetchDocumentExternalInfoPath(documentId: number): string {
    return `/document/externalinfo?documentid=${documentId.toString()}`;
}

// Document Ocr
export function createFetchDocumentOcrSummaryPath(documentId: number): string {
    return `/document/ocrsummary?documentid=${documentId.toString()}`;
}

export function createFetchDocumentOcrDataPath(documentId: number, page: number): string {
    return `/document/ocrpage?documentid=${documentId.toString()}&page=${page.toString()}`;
}

// Ocr Training
export function createFetchOcrTrainingPath(documentId: number): string {
    return `/document/ocrtraining?documentid=${documentId.toString()}`;
}

export function createDeleteOcrTrainingFieldPath(documentId: number, fieldId: string): string {
    return `/document/ocrtraining/field?documentid=${documentId.toString()}&fieldid=${fieldId}`;
}

export function createAddOcrTrainingFieldPath(documentId: number): string {
    return `/document/ocrtraining/field?documentid=${documentId.toString()}`;
}

export function createUpdateOcrTrainingFieldPath(documentId: number, fieldId: string): string {
    return `/document/ocrtraining/field?documentid=${documentId.toString()}&fieldid=${fieldId}`;
}

export function createApplyOcrTrainingPath(documentId: number): string {
    return `/document/ocrtraining/apply?documentid=${documentId.toString()}`;
}

export function createUpdateOcrTrainingSqlPath(documentId: number): string {
    return `/document/ocrtraining/posttrainingsql?documentid=${documentId.toString()}`;
}

export function createDeleteOcrTrainingSqlPath(documentId: number): string {
    return `/document/ocrtraining/posttrainingsql?documentid=${documentId.toString()}`;
}

export function createFetchOcrTrainingLogPath(documentId: number): string {
    return `/document/ocrtraining/gettraininglog?documentid=${documentId.toString()}`;
}

export function createFetchOcrCodingTrainingPath(documentId: number): string {
    return `/document/ocrtraining/codingtraining?documentid=${documentId.toString()}`;
}

export function createAddOcrCodingTrainingPath(documentId: number): string {
    return `/document/ocrtraining/codingtraining?documentid=${documentId.toString()}`;
}

export function createDeleteOcrCodingTrainingPath(documentId: number, searchText?: string): string {
    return `/document/ocrtraining/codingtraining?documentid=${documentId.toString()}${searchText ? `&searchtext=${searchText}` : ''}`;
}

// Document template sharing
export function createFetchSharedTemplatesPath(documentId: number, limitToEnabledTemplates: boolean): string {
    return `/document/ocrtraining/sharedtemplates?documentid=${documentId.toString()}&onlyEnabled=${limitToEnabledTemplates ? 'true' : 'false'}`;
}

export function createApplySharedTemplatePath(documentId: number, templateId: string): string {
    return `/document/ocrtraining/sharedtemplate/apply?documentid=${documentId.toString()}&templateid=${templateId}`;
}

export function createDeclineSharedTemplatePath(documentId: number, templateId: string): string {
    return `/document/ocrtraining/sharedtemplate/decline?documentid=${documentId.toString()}&templateid=${templateId}`;
}

export function createConfirmSharedTemplatePath(documentId: number, templateId: string): string {
    return `/document/ocrtraining/sharedtemplate/confirm?documentid=${documentId.toString()}&templateid=${templateId}`;
}

export function createSetEnabledSharedTemplatePath(documentId: number, templateId: string, enabled: boolean): string {
    return `/document/ocrtraining/sharedtemplate/setenabled?documentid=${documentId.toString()}&templateid=${templateId}&enable=${enabled ? 'true' : 'false'}`;
}

export function createSetGenericSharedTemplatePath(documentId: number, templateId: string, generic: boolean): string {
    return `/document/ocrtraining/sharedtemplate/setgenericerp?documentid=${documentId.toString()}&templateid=${templateId}&genericerp=${generic ? 'true' : 'false'}`;
}

export function createUpdateSharedTemplatePath(documentId: number, templateId: string, name: string): string {
    return `/document/ocrtraining/sharedtemplate/rename?documentid=${documentId.toString()}&templateid=${templateId}&newname=${name}`;
}

// Document dynamic assign
export function createFetchDocumentDynamicAssignPath(documentId: number): string {
    return `/document/assign?documentid=${documentId.toString()}`;
}

// Document annotations
export function createFetchDocumentAnnotationsPath(documentId: number): string {
    return `/document/annotations?documentid=${documentId.toString()}`;
}

export function createAddDocumentAnnotationPath(documentId: number): string {
    return `/document/annotations?documentid=${documentId.toString()}`;
}

export function createUpdateDocumentAnnotationPath(documentId: number, annotationId: number): string {
    return `/document/annotations?documentid=${documentId.toString()}&annotationid=${annotationId.toString()}`;
}

export function createDeleteDocumentAnnotationPath(documentId: number, annotationId: number): string {
    return `/document/annotations?documentid=${documentId.toString()}&annotationid=${annotationId.toString()}`;
}

export function batchDocumentAnnotationsPath(documentId: number): string {
    return `/document/batchannotations?documentid=${documentId.toString()}`;
}

// Document audits
export function createFetchDocumentAuditsPath(documentId: number): string {
    return `/document/audits?documentid=${documentId.toString()}`;
}

// Document attachments
export function createFetchDocumentAttachmentsPath(documentId: number): string {
    return `/document/attachments?documentid=${documentId.toString()}`;
}

// Document upload
export function createFetchDocumentUploadUrl(documentId: number): string {
    return `/document/upload?documentid=${documentId.toString()}`;
}

// Document link
export function createLinkDocumentPath(documentId: number, hmac: string, fileName: string): string {
    return `/document/upload?documentId=${documentId.toString()}&hmac=${hmac}&fileName=${encodeURIComponent(fileName)}`;
}

// Document actions
export function createFetchDocumentActionsPath(documentId: number): string {
    return `/document/actions?documentid=${documentId.toString()}`;
}

export function createPerformDocumentActionPath(documentId: number): string {
    return `/document/actions?documentid=${documentId}`;
}

export function createRotateDocumentPagePath(documentId: number): string {
    return `/document/rotate?documentId=${documentId}`;
}

export function createMoveDocumentPagePath(documentId: number): string {
    return `/document/movepage?documentId=${documentId}`;
}

export function createRemoveDocumentOwnerCheckPath(documentId: number): string {
    return `/document/removepages?documentId=${documentId}`;
}

export function createDeleteDocumentPagesPath(documentId: number): string {
    return `/document/removepages?documentId=${documentId}`;
}

// Link Documents
export function createFetchLinkDocumentsListPath(documentId: number, showLinkedDocuments: boolean, showDocumentsFromCurrentVendorOnly: boolean): string {
    return `/document/link/list?documentid=${documentId.toString()}&onlyunlinkeddocuments=${!showLinkedDocuments}&onlycurrentvendor=${showDocumentsFromCurrentVendorOnly}`;
}

export function createFetchLinkDocumentsMetaPath(): string {
    return '/document/link/meta';
}

export function createLinkDocumentsPath(documentId: number): string {
    return `/document/link?documentid=${documentId.toString()}`;
}

// Document coding
export function createFetchDocumentCodingPath(documentId: number): string {
    return `/document/coding?documentid=${documentId.toString()}`;
}

export function createFetchDocumentCodingDefaultsPath(documentId: number): string {
    return `/document/coding/defaults?documentid=${documentId.toString()}`;
}

export function createAddDocumentCodingLinePath(documentId: number): string {
    return `/document/coding?documentid=${documentId.toString()}`;
}

export function createUpdateDocumentCodingLinePath(documentId: number, lineId: number): string {
    return `/document/coding?documentid=${documentId.toString()}&rowid=${lineId.toString()}`;
}

export function createUpdateDocumentCodingLinesPath(documentId: number): string {
    return `/document/coding/bulk?documentid=${documentId.toString()}`;
}

export function createDeleteDocumentCodingLinePath(documentId: number): string {
    return `/document/coding?documentid=${documentId.toString()}`;
}

export function createFetchCopyCodingListPath(documentId: number): string {
    return `/copycoding/getlist?docid=${documentId.toString()}`;
}

export function createCopyCodingPath(documentId: number, fromDocId: number): string {
    return `/copycoding/copylines?docid=${documentId}&fromdocid=${fromDocId}`;
}

// Coding Accruals
export function createFetchCodingAccrualsPath(documentId: number): string {
    return `/document/coding/accruals?documentid=${documentId.toString()}`;
}

export function createUpdateCodingAccrualsPath(documentId: number, lineId: number): string {
    return `/document/coding/accruals?documentid=${documentId.toString()}&rowid=${lineId.toString()}`;
}

// Document Invoice Lines
export function createFetchDocumentInvoiceLinesPath(documentId: number): string {
    return `/document/matching?documentid=${documentId.toString()}`;
}

export function createAddDocumentInvoiceLinePath(documentId: number): string {
    return `/document/matching?documentid=${documentId.toString()}`;
}

export function createUpdateDocumentInvoiceLinePath(documentId: number, lineId: number): string {
    return `/document/matching?documentid=${documentId.toString()}&rowid=${lineId.toString()}`;
}

export function createDeleteDocumentInvoiceLinePath(documentId: number): string {
    return `/document/matching?documentid=${documentId.toString()}`;
}

// Document matching
export function createRefreshDocumentMatchingPath(documentId: number): string {
    return `/document/matching/refresh?documentid=${documentId.toString()}`;
}

export function createLinkDocumentMatchingLinePath(documentId: number): string {
    return `/document/matching/link?documentid=${documentId.toString()}`;
}

export function createUnlinkDocumentMatchingLinePath(documentId: number, rowId: number): string {
    return `/document/matching/link?documentid=${documentId.toString()}&rowid=${rowId.toString()}`;
}

export function createFetchDocumentMatchingOrderPath(documentId: number, filtered: boolean): string {
    return `/document/matching/orderlookup?documentid=${documentId.toString()}&filtered=${filtered ? 'true' : 'false'}`;
}

export function createCopyDocumentMatchingOrderLinePath(documentId: number): string {
    return `/document/matching/copy?documentid=${documentId.toString()}`;
}

export function createApproveMatchingDifferencePath(documentId: number): string {
    return `/document/matching/approvedifference?documentid=${documentId.toString()}`;
}

export function createRejectMatchingDifferencePath(documentId: number): string {
    return `/document/matching/rejectdifference?documentid=${documentId.toString()}`;
}

// Document file
export function createFetchFilePath(documentId: number): string {
    return `/document/file?documentid=${documentId.toString()}`;
}

export function createFetchFileRepairedPath(documentId: number): string {
    return `/document/filerepaired?documentid=${documentId.toString()}`;
}

export function createRepairFilePath(documentId: number): string {
    return `/document/repairfile?documentid=${documentId.toString()}`;
}

// Comments
export function createFetchCommentsPath(documentId: number): string {
    return `/document/comments?documentid=${documentId.toString()}`;
}

export function createAddCommentPath(documentId: number): string {
    return `/document/comments?documentid=${documentId.toString()}`;
}

// Creditor Validation
export function createFetchCreditorValidationPath(documentId: number): string {
    return `/document/creditorvalidation?documentid=${documentId.toString()}`;
}

// Lookup
interface PlaceholderReplacements {
    [key: string]: string;
}
export function createFetchLookupPath(lookup: string, params: Params): string {
    const regex = /{([a-zA-Z0-9_|]+)}/gm;
    const match = lookup.match(regex);

    const placeholderReplacements: PlaceholderReplacements = {};
    match?.forEach((placeholder) => {
        const field = placeholder.replace('{', '').replace('}', '').replace(OPTIONAL_KEY, '');
        placeholderReplacements[placeholder] = params[field] ? encodeURIComponent(params[field]) : '';
    });

    const url = replacePlaceholdersInString(lookup, placeholderReplacements);
    return `/${url}`;
}

export function createFetchCachedLookupPath(lookup: string, params: Params): string {
    const url = createFetchLookupPath(lookup, params);
    const cachedUrl = url.replace('lookup', 'lookupcached').replace('lookupcached/coding', 'lookup/codingcached');
    return cachedUrl;
}

export function createRefreshLookupPath(documentId: number, fieldName: string, key: string): string {
    return `/lookup/refresh?docid=${documentId}&fieldName=${fieldName}&Key=${key}`;
}

// Mailtemplates
export function createFetchMailTemplatesPath(): string {
    return '/mail/templates';
}

export function createFetchMailTemplatePath(templateId: number, documentId?: number): string {
    return `/mail/template/?documentId=${documentId}&templateId=${templateId}`;
}

// LineSource
export function createFetchLineSourcePath(documentId: number): string {
    return `/document/coding/linesource?documentid=${documentId.toString()}`;
}

export function createUpdateLineSourcePath(documentId: number): string {
    return `/document/coding/linesource?documentid=${documentId.toString()}`;
}

export function createSendMailPath(): string {
    return '/mail/send';
}

// Sam Flags
export function createFetchSamPath(documentId: number): string {
    return `/document/sam/?docid=${documentId.toString()}`;
}

// Apply Sam
export function createApplySamPath(documentId: number): string {
    return `/document/sam/apply?docid=${documentId.toString()}`;
}

// Update Sam Always
export function createUpdateSamAlwaysPath(documentId: number): string {
    return `/document/sam/always?docid=${documentId.toString()}`;
}

export function createAddSamNeverPath(documentId: number): string {
    return `/document/sam/never?docid=${documentId.toString()}`;
}

export function createDeleteSamNeverPath(documentId: number): string {
    return `/document/sam/never?docid=${documentId.toString()}`;
}

// Sam Preview
export function createFetchSamPreviewPath(documentId: number): string {
    return `/document/sam/preview/?docid=${documentId.toString()}`;
}

// Sam Coding Line Properties
export function createFetchSamCodingLinePropsPath(documentId: number): string {
    return `/document/sam/getcodinglineproperties?docid=${documentId.toString()}`;
}

// Sam BackOffice Status Help
export function createFetchSamBackOfficeStatusHelpPath(): string {
    return '/document/sam/automationserver';
}

// Versions
export function createFetchVersionPath(): string {
    return '/version';
}

// Diagnostics
export function createFetchDiagnosticsPath(): string {
    return '/diagnostics';
}

// Field Purchases
export function createFetchFieldPurchasesMetaPath(): string {
    return '/fieldpurchases/metadata';
}

// Replacements
export function createFetchReplacementsPath(): string {
    return '/replacements';
}

export function createUpdateReplacementsPath(): string {
    return '/replacements';
}

export function createDeleteReplacementsPath(): string {
    return '/replacements';
}

// Replacements for other users
export function createFetchReplacementsForOtherUsersPath(): string {
    return '/admin/replacements';
}

export function createCreateReplacementForOtherUserPath(): string {
    return `/admin/replacements`;
}

export function createDeleteReplacementsForOtherUsersPath(userId: string): string {
    return `/admin/replacements/${userId}`;
}

// News
export function createFetchNewsPath(): string {
    return `${window.location.origin}/news.json`;
}

// Maintenance message
export function createFetchMaintenanceMessagePath(): string {
    return `${window.location.origin}/maintenance.json`;
}

// File handler2
export function createUploadToDocbasePath(): string {
    return '/files/uploadtodocbase';
}

export function createConvertToPdfPath(): string {
    return '/files/converttopdf';
}

export function createFetchFileHandlerAdministrations(): string {
    return '/files/administrations';
}

// Log
export function createAddLogLinePath(): string {
    return '/log';
}

// Inconto Authentication
export function createAuthenticateIncontoPath(): string {
    return '/authinconto';
}

// Routing Table
export function createFetchRoutingTablePath(): string {
    return '/routingtable';
}

export function createAddRoutingTableRowPath(): string {
    return '/routingtable';
}

export function createDeleteRoutingTableRowPath(): string {
    return '/routingtable';
}

export function createUpdateRoutingTableRowPath(rowId: number): string {
    return `/routingtable?id=${rowId.toString()}`;
}

// Sam BackOffice Status Help
export function createSamCollectBackOfficeStatus(): string {
    return '/document/sam/ai';
}
